import { useEffect, useState } from 'react';

const LOCAL_STORAGE_CHANGE_EVENT = 'local-storage-change';

/**
 * @template T
 * @param {string} key
 * @param {T} initialValue
 * @returns {[T, React.Dispatch<React.SetStateAction<T>>]}
 */
export function useLocalStorage(key, initialValue) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateStoredValue = (serializedValue) => {
      try {
        setStoredValue(
          serializedValue === null ? initialValue : JSON.parse(serializedValue),
        );
      } catch (error) {
        console.error(error);
      }
    };
    const handleStorageChange = (event) => {
      if (event.key === key) updateStoredValue(event.newValue);
    };
    const handleLocalStorageChange = (event) => {
      if (event.detail?.key === key) updateStoredValue(event.detail.value);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleLocalStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        LOCAL_STORAGE_CHANGE_EVENT,
        handleLocalStorageChange,
      );
    };
  }, [key, initialValue]);
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== 'undefined') {
        const serializedValue = JSON.stringify(valueToStore);
        window.localStorage.setItem(key, serializedValue);
        window.dispatchEvent(
          new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
            detail: { key, value: serializedValue },
          }),
        );
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };
  return [storedValue, setValue];
}
